<!--
Copyright (C) 2026 Checkmk GmbH - License: Checkmk Enterprise License
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'

import usei18n from '@/lib/i18n'

import CmkButton from '@/components/CmkButton'
import CmkMultitoneIcon from '@/components/CmkIcon/CmkMultitoneIcon.vue'
import CmkScrollContainer from '@/components/CmkScrollContainer.vue'
import CmkParagraph from '@/components/typography/CmkParagraph.vue'

import { blockComponent } from '@/nonfree/ai/AiBlock'
import type { Conversation, ConversationError } from '@/nonfree/ai/control-plane/conversation'

const props = defineProps<{
  conversation: Conversation
}>()

const { _t } = usei18n()

const prompt = ref('')
const messages = useTemplateRef('messages')
const promptInput = useTemplateRef('promptInput')
const followOutput = ref(true)

function describeConversationError(error: ConversationError): string {
  switch (error.kind) {
    case 'incomplete':
      return _t('The answer ended before it was complete.')
    case 'failed':
    case 'request':
      return error.message
  }
}

const meta = computed(() => {
  const error = props.conversation.error.value
  return [
    `id: ${props.conversation.id.value ?? '-'}`,
    `status: ${props.conversation.status.value}`,
    `error: ${error ? describeConversationError(error) : '-'}`
  ].join(' | ')
})

const canSend = computed(
  () => prompt.value.trim() !== '' && props.conversation.status.value !== 'streaming'
)

async function send(): Promise<void> {
  if (!canSend.value) {
    return
  }
  const text = prompt.value
  prompt.value = ''
  followOutput.value = true
  await props.conversation.send(text)
}

function onScroll(): void {
  const container = messages.value?.containerRef
  if (!container) {
    return
  }
  followOutput.value = container.scrollHeight - container.scrollTop - container.clientHeight <= 2
}

function scrollToBottom(): void {
  const container = messages.value?.containerRef
  if (container) {
    container.scrollTop = container.scrollHeight
  }
}

onMounted(scrollToBottom)

watch(
  // The last block is replaced, not changed, while its text streams in.
  () => {
    const blocks = props.conversation.blocks.value
    return [props.conversation, blocks[blocks.length - 1]]
  },
  () => {
    if (followOutput.value) {
      scrollToBottom()
    }
  },
  { flush: 'post' }
)

defineExpose({ focusPrompt: () => promptInput.value?.focus() })
</script>

<template>
  <div class="ai-assistant-chat">
    <CmkParagraph>{{ meta }}</CmkParagraph>
    <CmkScrollContainer ref="messages" class="ai-assistant-chat__messages" @scroll="onScroll">
      <component
        :is="blockComponent(block)"
        v-for="(block, index) in conversation.blocks.value"
        :key="index"
        :block="block"
      />
    </CmkScrollContainer>
    <CmkButton v-if="conversation.status.value === 'failed'" @click="conversation.retry()">
      {{ _t('Retry') }}
    </CmkButton>

    <div class="ai-assistant-chat__prompt">
      <textarea
        ref="promptInput"
        v-model="prompt"
        class="ai-assistant-chat__prompt-input"
        rows="4"
        maxlength="10000"
        :placeholder="_t('Ctrl+Enter to send')"
        @keydown.enter.ctrl.prevent="send"
        @keydown.enter.meta.prevent="send"
      ></textarea>
      <CmkButton
        class="ai-assistant-chat__send"
        size="iconOnly"
        :title="_t('Send')"
        :aria-label="_t('Send')"
        :disabled="!canSend"
        @click="send"
      >
        <CmkMultitoneIcon
          name="arrow-down"
          :primary-color="{ custom: 'var(--white)' }"
          :rotate="180"
          size="small"
        />
      </CmkButton>
    </div>
  </div>
</template>

<style scoped>
.ai-assistant-chat {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.ai-assistant-chat__messages {
  flex: 1;
  min-height: 0;
  font-size: var(--font-size-small);
}

.ai-assistant-chat__prompt {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--dimension-4);
  padding: var(--dimension-4);
  background: var(--default-form-element-bg-color);
  border: 1px solid transparent;
  border-radius: var(--border-radius);
}

.ai-assistant-chat__prompt:focus-within {
  border-color: var(--ai-purple);
}

.ai-assistant-chat__send {
  --default-button-optional-color: var(--ai-purple);
  --button-optional-border-color: var(--ai-purple);
}

.ai-assistant-chat__prompt-input {
  align-self: stretch;
  padding: 0;
  font: inherit;
  color: var(--font-color);
  background: transparent;
  border: none;
  outline: none;
  resize: none;
}
</style>
